import { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import { OfferItem, Position } from '@recruitee/http/types';
import { createEntityCollectionStore, memoizeSelector, pipeState } from '@recruitee/ngrx';

import { EntityStoreKey, OfferScope } from '../../types';

const { actions, namespace, selectors, reducer } = createEntityCollectionStore<OfferItem>()(
  EntityStoreKey.TalentPools,
  {
    customActions: adapter => ({
      fetchCollection: (state, payload: { scope: OfferScope }) =>
        adapter.markAsPending(state, { scope: payload.scope, entities: [] }),
      fetchActiveCollection: state =>
        adapter.markAsPending(state, { scope: OfferScope.Active, entities: [] }),
      fetchArchivedCollection: state =>
        adapter.markAsPending(state, { scope: OfferScope.Archived, entities: [] }),
      upsertOne: (state, payload: { data: OfferItem; scope: OfferScope }) =>
        adapter.upsertOne(state, payload.data, { scope: payload.scope }),
      updatePositions: (state, payload: Position[]) =>
        pipeState(state, next =>
          adapter.markAsPending(next, { scope: OfferScope.Active, entities: [] }),
        ),
      updatePositionSuccess: (state, payload: Position[]) => {
        const updated = [...payload]
          .sort((a, b) => b.position - a.position)
          .map(({ id, position }) => ({
            ...state.entityMap[id]!,
            position: position,
          }));
        return pipeState(
          state,
          next => adapter.setMany(next, updated, { scope: OfferScope.Active }),
          next => adapter.markAsLoaded(next, { scope: OfferScope.Active, entities: [] }),
        );
      },
      updatePositionsFailed: (state, payload: { error: HttpErrorResponse }) =>
        pipeState(state, next =>
          adapter.setError(next, payload.error, { scope: OfferScope.Active, entities: [] }),
        ),
      references: (state, payload: { data: OfferItem[] }) =>
        adapter.updateOrCreateMany(state, payload.data),
    }),
  },
);

// entities
const active = createSelector(selectors.entities({ scope: OfferScope.Active }), state => state);
const archived = createSelector(selectors.entities({ scope: OfferScope.Archived }), state => state);
const activeFollowed = createSelector(active, state =>
  state.filter(talentPool => talentPool.followed),
);

const all = createSelector(active, archived, (active, archived) => [...active, ...archived]);

const bySlug = memoizeSelector((slug: string) =>
  createSelector(all, (state: OfferItem[]) => state.find(offer => offer.slug === slug)),
);
// meta
const activeScopeIsLoaded = createSelector(
  selectors.isLoaded({ scope: OfferScope.Active }),
  state => state,
);
const archivedScopeIsLoaded = createSelector(
  selectors.isLoaded({ scope: OfferScope.Archived }),
  state => state,
);

const customSelectors = {
  all,
  active,
  archived,
  activeFollowed,
  activeScopeIsLoaded,
  archivedScopeIsLoaded,
  bySlug,
  canAssignTo: active,
};

const talentPoolsSelectors = {
  ...selectors,
  ...customSelectors,
};

export {
  actions as TalentPoolItemsActions,
  namespace as TalentPoolItemsNamespace,
  talentPoolsSelectors as TalentPoolItemsSelectors,
  reducer as TalentPoolItemsReducer,
};
